import React, { useState } from 'react';
import { 
  ArrowLeft, Search, Filter, Plus, 
  ClipboardCheck, PackageCheck, Microscope, Truck, CalendarCheck
} from 'lucide-react';
import { inspectionPlans, inspectionTypes } from '../../data/qualityControlData';

const InspectionPlansList = ({ onBack, onViewDetail }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Get inspection type icon
  const getInspectionTypeIcon = (type) => {
    const inspectionType = inspectionTypes.find(t => t.id === type);
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'in-process':
        return <Microscope className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'outgoing':
        return <Truck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'periodic':
        return <CalendarCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-blue-500" />;
    }
  };
  
  // Filter plans
  const filteredPlans = (inspectionPlans || []).filter(plan => {
    const matchesSearch = 
      plan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || plan.inspection_type === filterType;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">檢驗計劃</h1>
        </div>
        <button
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          新增計劃
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder="搜尋計劃名稱或描述..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center space-x-2 min-w-[200px]">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">所有檢驗類型</option>
            {(inspectionTypes || []).map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlans.map(plan => {
          const inspectionType = inspectionTypes.find(t => t.id === plan.inspection_type) || { name: '其他檢驗', color: '#6B7280' };
          
          return (
            <div 
              key={plan.id} 
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onViewDetail(plan.id)}
            >
              <div className="p-6 border-b border-gray-100 flex items-center">
                <div className="p-2 rounded-md mr-3" style={{ backgroundColor: `${inspectionType.color}20` }}>
                  {getInspectionTypeIcon(plan.inspection_type)}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500">{inspectionType.name}</p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{plan.description}</p>
                
                <div className="flex justify-between text-sm">
                  <div>
                    <span className="text-gray-500">檢驗項目:</span>
                    <span className="ml-1 font-medium">{plan.checkpoints?.length || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">抽樣方法:</span>
                    <span className="ml-1 font-medium">{plan.sampling_method}</span>
                  </div>
                </div>
                
                <div className="mt-4 flex flex-wrap gap-2">
                  {plan.product_categories?.map(item => (
                    <span 
                      key={item} 
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700"
                    >
                      {item === 'raw-material' ? '原材料' : 
                       item === 'semi-finished' ? '半成品' : 
                       item === 'finished' ? '成品' : item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {filteredPlans.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500 border border-gray-100">
          沒有符合條件的檢驗計劃
        </div>
      )}
    </div>
  );
};

export default InspectionPlansList;
